import { useEffect, useMemo, useState } from 'react';
import { TrashIcon, UploadSimpleIcon } from '@phosphor-icons/react';
import type { AssetUsage } from '@admin-panel/types';
import { Button, Card, CardContent, CardFooter } from '@admin-panel/ui';
import { cn } from '@admin-panel/ui';
import type { UploadFile } from 'antd/es/upload/interface';
import AssetUploader from './index';

type UploadAssetFile = UploadFile & {
  id?: number;
  response?: {
    id?: number;
    url?: string;
    name?: string;
    type?: string;
    usage?: string;
    uploaded?: boolean;
  };
};

type FileUpload01Props = {
  usage: AssetUsage;
  title: string;
  description: string;
  fileList: Array<UploadAssetFile>;
  onChange: (fileList: Array<UploadAssetFile>) => void;
  onUploadStart?: () => void;
  onUploadEnd?: () => void;
  accept?: string;
  disabled?: boolean;
  isLoading?: boolean;
};

const FileUpload01 = ({
  usage,
  title,
  description,
  fileList,
  onChange,
  onUploadStart,
  onUploadEnd,
  accept = 'image/*',
  disabled,
  isLoading,
}: FileUpload01Props) => {
  const [localPreviewUrl, setLocalPreviewUrl] = useState<string | null>(null);

  const currentFile = fileList[0];
  const remotePreviewUrl =
    currentFile?.url || currentFile?.thumbUrl || currentFile?.response?.url || null;
  const previewUrl = localPreviewUrl || remotePreviewUrl;
  const fileName = currentFile?.name || currentFile?.response?.name || null;
  const fileSize =
    typeof currentFile?.size === 'number' ? `${Math.round(currentFile.size / 1024)} KB` : null;
  const uploadProgress =
    typeof currentFile?.percent === 'number'
      ? currentFile.percent
      : currentFile?.status === 'done'
        ? 100
        : 0;
  const isBusy = Boolean(currentFile?.status === 'uploading' || isLoading || disabled);

  useEffect(() => {
    if (localPreviewUrl && remotePreviewUrl) {
      URL.revokeObjectURL(localPreviewUrl);
      setLocalPreviewUrl(null);
    }
  }, [localPreviewUrl, remotePreviewUrl]);

  useEffect(
    () => () => {
      if (localPreviewUrl) {
        URL.revokeObjectURL(localPreviewUrl);
      }
    },
    [localPreviewUrl],
  );

  const removeFile = () => {
    if (localPreviewUrl) {
      URL.revokeObjectURL(localPreviewUrl);
      setLocalPreviewUrl(null);
    }

    onChange([]);
  };

  const helperText = useMemo(() => `Supported formats: JPG, PNG, SVG.`, []);

  return (
    <Card className="gap-0 rounded-lg border border-border/50 py-0 shadow-none ring-0">
      <CardContent className="p-0">
        <div className="px-4 pt-4 pb-3 text-center">
          <h3 className="m-0 text-sm font-medium text-foreground">{title}</h3>
          <p className="mt-1 mb-0 text-xs text-muted-foreground">{description}</p>
        </div>

        <div className="px-4 pb-4">
          <AssetUploader
            usage={usage}
            maxCount={1}
            multiple={false}
            accept={accept}
            showUploadList={false}
            fileList={fileList}
            onChange={(info: { fileList: Array<UploadFile> }) => {
              const nextFile = info.fileList[0];
              const originFile = nextFile?.originFileObj;

              if (originFile instanceof File) {
                const nextPreviewUrl = URL.createObjectURL(originFile);
                setLocalPreviewUrl(previous => {
                  if (previous) {
                    URL.revokeObjectURL(previous);
                  }

                  return nextPreviewUrl;
                });
              }

              onChange(
                info.fileList.slice(0, 1).map(file => ({
                  ...file,
                  id:
                    'id' in file && typeof (file as UploadAssetFile).id === 'number'
                      ? (file as UploadAssetFile).id
                      : undefined,
                  response: file.response
                    ? {
                        ...file.response,
                        id:
                          typeof (file.response as { id?: unknown }).id === 'number'
                            ? ((file.response as { id?: number }).id ?? undefined)
                            : undefined,
                      }
                    : undefined,
                })),
              );
            }}
            onUploadStart={onUploadStart}
            onUploadEnd={onUploadEnd}
            disabled={disabled}
            isLoading={isLoading}
          >
            <div
              className={cn(
                'flex cursor-pointer flex-col items-center justify-center rounded-md border-2 border-dashed border-border px-4 py-6 text-center transition-colors',
                isBusy
                  ? 'cursor-not-allowed border-border/60 bg-muted/10 opacity-70'
                  : 'border-border/70 bg-muted/10 hover:border-border hover:bg-muted/20',
              )}
            >
              <div className="mb-2 rounded-full bg-muted p-3">
                <UploadSimpleIcon size={18} className="text-muted-foreground" />
              </div>
              <p className="m-0 text-sm font-medium text-foreground">{title}</p>
              <p className="mt-1 mb-0 text-xs text-muted-foreground">
                or{' '}
                <span className="font-medium text-foreground underline underline-offset-4">
                  click to browse
                </span>
              </p>
            </div>
          </AssetUploader>
        </div>

        {currentFile ? (
          <div className="px-4 pb-4">
            <div className="rounded-md border border-border/50 bg-muted/[0.03] p-3">
              <div className="flex flex-col items-center gap-3 text-center">
                <div className="h-24 w-20 overflow-hidden rounded-sm bg-muted">
                  {previewUrl ? (
                    <img
                      src={previewUrl}
                      alt={fileName || title}
                      className="h-full w-full object-cover"
                    />
                  ) : null}
                </div>

                <div className="w-full max-w-56">
                  <div className="flex flex-col items-center gap-1">
                    <div className="min-w-0">
                      <p className="m-0 truncate text-sm text-foreground">{fileName}</p>
                      {fileSize ? (
                        <p className="m-0 text-xs text-muted-foreground">{fileSize}</p>
                      ) : null}
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="small"
                      icon={<TrashIcon size={14} />}
                      onClick={removeFile}
                    />
                  </div>

                  <div className="mt-2 flex items-center gap-2">
                    <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
                      <div
                        className="h-full bg-primary transition-[width] duration-200 ease-out"
                        style={{ width: `${uploadProgress || 100}%` }}
                      />
                    </div>
                    <span className="text-[11px] text-muted-foreground">
                      {Math.round(uploadProgress || 100)}%
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </CardContent>

      <CardFooter className="justify-center rounded-b-lg border-t border-border/40 bg-muted/15 px-4 py-3 text-center">
        <p className="m-0 text-xs text-muted-foreground">{helperText}</p>
      </CardFooter>
    </Card>
  );
};

export default FileUpload01;
